const loaders = new WeakMap();
const starts = new WeakMap();
const context = vm => JSON.stringify([vm.$route.fullPath, vm.idPaciente, vm.idAgenda, vm.idAtendimento]);

export async function consultarFichas(api, jwt, recurso, parametros) {
  if(!jwt.getToken()) { throw new Error("Sessão indisponível para consultar as fichas."); }
  const { signal, ...body } = parametros;
  api.setHeader();
  const { data } = await api.postOnce(`atendimentos/access/${recurso}`, body, { signal, timeout: 120000 });
  const catalogo = recurso === "listarFichasDadosMedico";
  const rows = catalogo ? data?.FICHAS : data;
  if(!Array.isArray(rows) || rows.some(row => !row || !row.ID_ATENDIMENTO_TIPO ||
    (!catalogo && (!Array.isArray(row.ITEMS) || row.ITEMS.some(item => !item || typeof item !== "object"))))) {
    throw new Error("Resposta inválida ao consultar as fichas.");
  }
  return rows;
}

export function fichasLoader(vm) {
  if(!loaders.has(vm)) { loaders.set(vm, criarFichasLoader(vm)); }
  return loaders.get(vm);
}

export function criarFichasLoader(vm) {
  let current = null, disposed = false, last = null;
  const valid = request => !disposed && current === request && request.context === context(vm);
  const loading = value => {
    vm.carregandoFichas = value;
    vm.$refs.atendimentoInclusao?.carregandoFichas(value);
  };
  function carregar(fichas = null, iniciar = true, paginasProntas = null) {
    if(disposed) { return Promise.resolve(false); }
    const snapshot = fichas === null ? null : JSON.parse(JSON.stringify(fichas));
    const key = JSON.stringify([snapshot, iniciar, paginasProntas !== null]);
    if(current?.promise && valid(current) && current.key === key && current.paginasProntas === paginasProntas) { return current.promise; }
    current?.controller.abort();
    const request = { key, paginasProntas, context: context(vm), controller: new AbortController(), promise: null };
    current = request;
    last = { fichas: snapshot, iniciar, paginasProntas, context: request.context };
    vm.erroFichas = "";
    loading(true);
    request.promise = Promise.resolve().then(async () => {
      try {
        if(!valid(request)) { return false; }
        const signal = request.controller.signal;
        const dadosHoje = vm.dadosAtendimentoPendentes
          ? await vm.getAtendimentosHoje({ ID_PACIENTE: vm.idPaciente, ID_ATENDIMENTO: vm.idAtendimento, ...(vm.idAgenda ? { ID_AGENDA: vm.idAgenda } : {}), signal }) : undefined;
        if(!valid(request)) { return false; }
        const selected = paginasProntas !== null ? null : (snapshot ?? await vm.consultarFichasDoAtendimento({ ID_AGENDA: vm.idAgenda, ID_ATENDIMENTO: vm.idAtendimento, signal }));
        if(!valid(request)) { return false; }
        const pages = paginasProntas ?? await vm.consultarCamposDasFichas({ FICHAS: selected, signal });
        if(!valid(request)) { return false; }
        const editor = vm.$refs.atendimentoInclusao;
        if(vm.idAtendimento && editor?.atendimentoIncluindo) {
          const applied = await editor.inicializarFichaDados({ paginas: pages, ...(dadosHoje === undefined ? {} : { dadosHoje }), preservar: true, vigente: () => valid(request) });
          if(applied === false || !valid(request)) { return false; }
        }
        if(dadosHoje !== undefined) { vm.dadosAtendimentoPendentes = false; }
        vm.atendimentosTipoItens = pages;
        if(paginasProntas === null && snapshot === null && vm.$refs.atendimentoItemLateral) { vm.$refs.atendimentoItemLateral.fichasDados = selected; }
        if(iniciar && !editor?.atendimentoIncluindo && !vm.atendimentoIncluindo) {
          await vm.$nextTick();
          if(!valid(request)) { return false; }
          // A leitura terminou. Não repetir escritas pelo botão de tentar novamente.
          last = null;
          await vm.iniciarAtendimento(null, false, true);
        }
        return true;
      } catch(error) {
        if(valid(request)) { vm.erroFichas = "Não foi possível carregar os dados e as fichas do atendimento. Os dados em edição foram mantidos. Tente novamente."; }
        return false;
      } finally {
        request.promise = null;
        // O início pode atribuir um ID de atendimento; a posse da leitura continua sendo esta.
        if(!disposed && current === request) { loading(false); }
      }
    });
    return request.promise;
  }
  return {
    carregar,
    tentarNovamente() {
      if(!last || last.context !== context(vm)) { return Promise.resolve(false); }
      return carregar(last.fichas, last.iniciar, last.paginasProntas);
    },
    cancelar() {
      current?.controller.abort();
      current = null;
      last = null;
      vm.erroFichas = "";
      loading(false);
    },
    encerrar() { disposed = true; current?.controller.abort(); current = null; loading(false); }
  };
}

export function iniciarAtendimentoUnico(vm, iniciar, vigente = () => true) {
  if(starts.has(vm)) { return starts.get(vm); }
  const promise = Promise.resolve().then(iniciar).finally(() => {
    starts.delete(vm);
    if(vigente()) { vm.isLoadingAtendimento = false; }
  });
  starts.set(vm, promise);
  return promise;
}

// Preserva valores do editor no momento da aplicação, inclusive edições durante a leitura.
export function preservarEdicaoFichas(novas, anteriores) {
  const key = pagina => JSON.stringify([String(pagina.ID_ATENDIMENTO_TIPO), String(pagina.TPC), String(pagina.PAGINA)]);
  const pages = new Map(anteriores.map(pagina => [key(pagina), pagina]));
  for(const pagina of novas) {
    const old = pages.get(key(pagina));
    if(!old) { continue; }
    if(old.ID_ATENDIMENTO_DETALHE) { pagina.ID_ATENDIMENTO_DETALHE = old.ID_ATENDIMENTO_DETALHE; }
    const fields = new Map(old.ITEMS.map(item => [String(item.SEQUENCIA), item]));
    for(const item of pagina.ITEMS) {
      const previous = fields.get(String(item.SEQUENCIA));
      if(!previous || previous.CAMPO_TIPO !== item.CAMPO_TIPO) { continue; }
      for(const name of ["CONTEUDO", "TEXTO_LIVRE"]) {
        if(Object.prototype.hasOwnProperty.call(previous, name)) { item[name] = previous[name]; }
      }
    }
  }
  return novas;
}
